import {useTranslation} from 'react-i18next'
import {DateField} from '@/components/date-field'
import {Fact} from '@/components/fact'
import {BoolField, FieldGroup, NumberField, SelectField} from '@/components/wizard-fields'
import {
  strToBool,
  type Draft,
  type PerfilSociodemografico as PerfilRead,
  type PerfilSociodemograficoUpdate,
} from '@/hooks/empresa/mi_espacio/use-perfil-sociodemografico'

const TIPO_CONTRATO = ['indefinido', 'fijo', 'obra_labor', 'aprendizaje_sena', 'prestacion_servicios', 'otro'] as const
const MODALIDAD = ['presencial', 'hibrido', 'remoto'] as const
const TRANSPORTE = ['moto', 'carro', 'transporte_publico', 'bicicleta', 'a_pie', 'ninguno'] as const

/** `maxFechaIngreso` llega resuelta del padre: solo ese archivo está en la exclusión de semgrep
 * `browser-clock-business-date`. */
export function LaboralStepFields({
  draft,
  perfil,
  godReadOnly,
  maxFechaIngreso,
  updateField,
  commit,
}: {
  draft: Draft
  perfil: PerfilRead
  godReadOnly: boolean
  maxFechaIngreso: string
  updateField: (field: keyof Draft, value: string) => void
  commit: (fields: Partial<PerfilSociodemograficoUpdate>) => void
}) {
  const {t} = useTranslation()
  return (
    <FieldGroup>
      <DateField
        id="perfil-sd-fecha-ingreso"
        label={t('perfilSociodemografico.fechaIngreso')}
        value={draft.fecha_ingreso}
        max={maxFechaIngreso}
        disabled={godReadOnly}
        onChange={(v) => {
          updateField('fecha_ingreso', v)
          commit({fecha_ingreso: v || null})
        }}
      />
      {perfil.antiguedad_anios !== null && perfil.antiguedad_anios !== undefined && (
        <Fact label={t('perfilSociodemografico.antiguedad')}>
          <span data-testid="perfil-sociodemografico-antiguedad">
            {t('perfilSociodemografico.antiguedadValue', {count: perfil.antiguedad_anios})}
          </span>
        </Fact>
      )}
      <SelectField
        id="perfil-sd-tipo-contrato"
        label={t('perfilSociodemografico.tipoContrato')}
        value={draft.tipo_contrato}
        options={TIPO_CONTRATO}
        optionLabel={(o) => t(`perfilSociodemografico.tipoContrato.${o}`)}
        unspecifiedLabel={t('perfilSociodemografico.unspecified')}
        disabled={godReadOnly}
        onChange={(v) => {
          updateField('tipo_contrato', v)
          commit({tipo_contrato: (v || null) as PerfilSociodemograficoUpdate['tipo_contrato']})
        }}
      />
      <SelectField
        id="perfil-sd-modalidad"
        label={t('perfilSociodemografico.modalidad')}
        value={draft.modalidad}
        options={MODALIDAD}
        optionLabel={(o) => t(`perfilSociodemografico.modalidad.${o}`)}
        unspecifiedLabel={t('perfilSociodemografico.unspecified')}
        disabled={godReadOnly}
        onChange={(v) => {
          updateField('modalidad', v)
          commit({modalidad: (v || null) as PerfilSociodemograficoUpdate['modalidad']})
        }}
      />
      <SelectField
        id="perfil-sd-transporte"
        label={t('perfilSociodemografico.transporte')}
        value={draft.transporte_principal}
        options={TRANSPORTE}
        optionLabel={(o) => t(`perfilSociodemografico.transporte.${o}`)}
        unspecifiedLabel={t('perfilSociodemografico.unspecified')}
        disabled={godReadOnly}
        onChange={(v) => {
          updateField('transporte_principal', v)
          commit({transporte_principal: (v || null) as PerfilSociodemograficoUpdate['transporte_principal']})
        }}
      />
      <BoolField
        id="perfil-sd-visita-clientes"
        label={t('perfilSociodemografico.visitaClientes')}
        value={draft.visita_clientes}
        disabled={godReadOnly}
        onChange={(v) => {
          updateField('visita_clientes', v)
          commit({visita_clientes: strToBool(v)})
        }}
      />
      <NumberField
        id="perfil-sd-minutos-desplazamiento"
        label={t('perfilSociodemografico.minutosDesplazamiento')}
        value={draft.minutos_desplazamiento}
        min={0}
        max={600}
        disabled={godReadOnly}
        onChange={(v) => updateField('minutos_desplazamiento', v)}
        onCommit={(v) => commit({minutos_desplazamiento: v})}
      />
    </FieldGroup>
  )
}
